import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions } from '@microsoft/signalr';
import { from, fromEventPattern, Observable, Subject } from 'rxjs';
import { catchError, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import {
    AccessDeniedError,
    hasMessage,
    IAuthTokenService,
    ILogContext,
    IMobizStateService,
    IServerApiBuilderService,
    LoggerService,
    LogLevel,
    MobizServerApi,
} from '../../core';
import { Inject, Injectable, InjectionToken, Optional } from '../../injection';
import { VOID } from '../../rxjs-utils';

import { Notification } from '../data/notification.do';
import { PulseNotificationHubMethod } from '../other/pulse-notification-hub-method.enum';
import { PULSE_NOTIFICATIONS_LOG_LEVEL, SignalRLogLevel } from '../other/signalr-log-level.enum';

function getNotConnectedError(): Error {
  return new Error('The notifications hub connection is not initialized.');
}

export interface IPulseNotificationsService {
  hubConnectionState: HubConnectionState | undefined;

  connect(): Observable<void>;

  disconnect(): Observable<void>;

  getMobizNotificationEvents(): Observable<Notification>;

  subscribeToPersonalNotifications(): Observable<void>;

  unsubscribeFromPersonalNotifications(): Observable<void>;
}

export const PULSE_NOTIFICATIONS_SERVICE: InjectionToken<IPulseNotificationsService>
  = new InjectionToken<IPulseNotificationsService>('PULSE_NOTIFICATIONS_SERVICE');

@Injectable()
export class PulseNotificationsService implements IPulseNotificationsService {
  private hubConnection: HubConnection | undefined;
  private removeActiveMethodHandlers$: Subject<void> = new Subject<void>();
  private subscribedToPersonalNotifications: boolean = false;

  public get hubConnectionState(): HubConnectionState | undefined {
    return this.hubConnection ? this.hubConnection.state : undefined;
  }

  private get accessToken(): string {
    if (!this.authTokenService.accessToken) {
      throw new AccessDeniedError('Please log in.');
    }
    return this.authTokenService.accessToken;
  }

  private get userId(): string {
    if (this.stateService.currentUser.value === null) {
      throw new AccessDeniedError('Please log in.');
    }
    return this.stateService.currentUser.value.id;
  }

  constructor(
    @Inject(IAuthTokenService)
    private authTokenService: IAuthTokenService,
    @Inject(IServerApiBuilderService)
    private serverApiBuilderService: IServerApiBuilderService,
    @Inject(IMobizStateService)
    private stateService: IMobizStateService,
    @Inject(PULSE_NOTIFICATIONS_LOG_LEVEL) @Optional()
    private pulseNotificationsDebug: SignalRLogLevel | null,
    private loggerService: LoggerService,
  ) {
  }

  /**
   * @see https://docs.microsoft.com/en-us/aspnet/core/signalr
   * @see https://docs.microsoft.com/en-us/aspnet/core/signalr/javascript-client
   */
  public connect(): Observable<void> {
    if (!this.hubConnection) {
      this.hubConnection = this.createHubConnection();
    }
    if (this.hubConnection.state !== HubConnectionState.Disconnected) {
      return VOID;
    }
    return from(this.hubConnection.start()).pipe(
      catchError((error: unknown): Observable<void> => {
        if (!this.isFrequentConnectionError(error)) {
          const context: ILogContext = { exception: error };
          const message: string = 'Failed to connect to the notifications hub.';
          this.loggerService.log(LogLevel.ERROR, message, context);
        }
        // Ignore the error and continue.
        return VOID;
      }),
    );
  }

  /**
   * NOTE: TP-11009 The SignalR connection often fails in Ionic environments.
   */
  public isFrequentConnectionError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null || !hasMessage(error)) {
      return false;
    }
    const transportsError: string = 'Unable to connect to the server with any of the available transports.';
    if (!error.message.includes(transportsError)) {
      return false;
    }
    const webSocketsError = 'Error: There was an error with the transport.';
    if (!error.message.includes(webSocketsError)) {
      return false;
    }
    const serverSentEventsError = "Error: 'ServerSentEvents' is disabled by the client.";
    if (!error.message.includes(serverSentEventsError)) {
      return false;
    }
    const longPollingError = "Error: 'LongPolling' is disabled by the client.";
    if (!error.message.includes(longPollingError)) {
      return false;
    }
    return true;
  }

  public disconnect(): Observable<void> {
    if (!this.hubConnection) {
      return VOID;
    }
    if (this.hubConnection.state === HubConnectionState.Disconnected) {
      return VOID;
    }
    const hubConnection: HubConnection = this.hubConnection;
    return this.unsubscribeFromPersonalNotifications().pipe(
      mergeMap((): Observable<void> => {
        // Removes all handlers for the specified hub method.
        // NOTE: This only works on methods that use this EventEmitter, { @see PulseNotificationsService.getMobizNotificationEvents() }
        this.removeActiveMethodHandlers$.next();
        // Stops the hub connection.
        return from(hubConnection.stop());
      }),
    );
  }

  public getMobizNotificationEvents(): Observable<Notification> {
    return this.connect().pipe(
      switchMap(() => {
        return this.subscribeToPersonalNotifications();
      }),
      takeUntil(this.removeActiveMethodHandlers$),
      mergeMap((): Observable<unknown> => {
        return this.getHubMethodEventPattern(PulseNotificationHubMethod.ReceiveNotification);
      }),
      map((event: unknown): Notification => {
        return new Notification(event);
      }),
    );
  }

  public subscribeToPersonalNotifications(): Observable<void> {
    if (this.subscribedToPersonalNotifications || !this.isConnected()) {
      return VOID;
    }
    return this.callMethodOnHub(PulseNotificationHubMethod.Subscribe).pipe(
      map((): void => {
        this.subscribedToPersonalNotifications = true;
      }),
    );
  }

  public unsubscribeFromPersonalNotifications(): Observable<void> {
    if (!this.subscribedToPersonalNotifications || !this.isConnected()) {
      return VOID;
    }
    return this.callMethodOnHub(PulseNotificationHubMethod.Unsubscribe).pipe(
      map((): void => {
        this.subscribedToPersonalNotifications = false;
      }),
    );
  }

  private callMethodOnHub(method: PulseNotificationHubMethod): Observable<void> {
    return from(this.getHubConnection().invoke(method, this.userId));
  }

  private createHubConnection(): HubConnection {
    // NOTE: The SignalR library is not compatibly with strictNullChecks, and null is required to disable the logger.
    const logger: SignalRLogLevel | undefined = this.pulseNotificationsDebug !== null
      ? this.pulseNotificationsDebug
      : null as unknown as undefined;
    const options: IHttpConnectionOptions = {
      accessTokenFactory: (): string => {
        return this.accessToken;
      },
      logger: logger,
      transport: HttpTransportType.WebSockets,
    };
    const url: string = this.serverApiBuilderService.getUrl(MobizServerApi.NotificationsHub);
    return new HubConnectionBuilder().withUrl(url, options).build();
  }

  private getHubConnection(): HubConnection {
    if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
      throw getNotConnectedError();
    }
    return this.hubConnection;
  }

  private getHubMethodEventPattern(method: PulseNotificationHubMethod): Observable<unknown> {
    if (!this.isConnected()) {
      return VOID;
    }
    const hubConnection: HubConnection = this.getHubConnection();
    return fromEventPattern(
      (handler) => hubConnection.on(method, handler),
      (handler) => hubConnection.off(method, handler),
    );
  }

  private isConnected(): boolean {
    return this.hubConnection ? this.hubConnection.state === HubConnectionState.Connected : false;
  }
}
